import { GlobalAlertService } from './global-alert.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, Subject, tap, of } from 'rxjs';
import * as convertKeys from 'convert-keys';
import { ApiService } from './api.service';
import { ServiceAccount } from '../model/service-account.model';
import { SDPsDataObject, ServiceDeliveryPoint } from '../model/service-delivery-point.model';
import { TranslateService } from '@ngx-translate/core';
import { GenericService } from './generic.service';
import { RequestMethodEnum, RequestOptions } from '@model';
import { AtlasPagesService } from 'atlas-pages-loader';
import { HttpClient } from '@angular/common/http';
import { CookieService } from './cookie.service';

@Injectable()
export class ServiceDeliveryPointService {
  public readonly conflictError = new Subject<any[]>();
  public readonly serviceDeliveryPoints$ = new BehaviorSubject<SDPsDataObject>({ sdps: [], hasError: false });
  public readonly serviceDeliveryPoint$ = new BehaviorSubject<ServiceDeliveryPoint>(null);
  public readonly selectedServiceDeliveryPointId$ = new BehaviorSubject<string>(null);
  public readonly loadingServiceAccounts$ = new BehaviorSubject<boolean>(true);
  public readonly loadingServiceDeliveryPoint$ = new BehaviorSubject<boolean>(true);
  public sdpParentId: string;
  public readonly serviceAccounts$ = new BehaviorSubject<ServiceAccount[]>(null);
  private sdpsCache: Map<string, ServiceDeliveryPoint[]> = new Map();
  private pagesLoaderService: AtlasPagesService;

  constructor(
    private readonly apiService: ApiService,
    private readonly messageService: GlobalAlertService,
    private readonly translateService: TranslateService,
    private readonly genericService: GenericService,
    private readonly cookieService: CookieService,
    private readonly httpClient: HttpClient,
  ) {
    this.pagesLoaderService = new AtlasPagesService(
      cookieService.getMarketsMock(),
      cookieService.getEnocSession(),
      this.httpClient,
    );
  }

  public async getServiceDeliveryPoint(id: string): Promise<ServiceDeliveryPoint> {
    try {
      this.loadingServiceDeliveryPoint$.next(true);
      const serviceDeliveryPoint = await this.apiService.get(`service-delivery-points/${id}`);
      const sdp: ServiceDeliveryPoint = convertKeys.toCamel(serviceDeliveryPoint);
      this.serviceDeliveryPoint$.next({
        ...sdp,
        displayLabels: serviceDeliveryPoint.display_labels,
        descriptions: serviceDeliveryPoint.descriptions,
      });
      this.selectedServiceDeliveryPointId$.next(id);
      this.loadingServiceDeliveryPoint$.next(false);
      return serviceDeliveryPoint;
    } catch (err) {
      this.loadingServiceDeliveryPoint$.next(false);
      console.log('Could not load serviceDeliveryPoint.', err);
      const GET_SDP_ERROR = this.translateService.instant('utility.account.get.sdp.error');
      this.messageService.setError(GET_SDP_ERROR);
      throw err;
    }
  }

  public setSdpParentId(value: string): void {
    this.sdpParentId = value;
  }

  public getSdpParentId(): string {
    return this.sdpParentId;
  }

  public getServiceDeliveryPoints(
    siteId: string,
    isUpdatingList: boolean = true,
    forceSelectFirst: boolean = true,
    forceRefresh: boolean = false,
    selectSdpId?: string,
  ): Observable<ServiceDeliveryPoint[]> {
    if (forceRefresh) {
      this.sdpsCache.delete(siteId);
    } else if (this.isCacheValid(siteId)) {
      const cachedData = this.sdpsCache.get(siteId);

      if (isUpdatingList) {
        this.serviceDeliveryPoints$.next({
          sdps: cachedData,
          hasError: false,
          selectFirst: forceSelectFirst,
          selectSdpId: selectSdpId,
          isLoading: false,
        });
      }

      if (selectSdpId) {
        this.selectSDP(selectSdpId);
      }

      return of(cachedData);
    }

    if (isUpdatingList) {
      this.serviceDeliveryPoints$.next({
        sdps: [],
        hasError: false,
        selectFirst: forceSelectFirst,
        selectSdpId: selectSdpId,
        isLoading: true,
      });
    }

    const options: RequestOptions<undefined, ServiceDeliveryPoint[]> = {
      method: RequestMethodEnum.GET,
      path: `/sites/${siteId}/service-delivery-points`,
    };

    return this.genericService.request(options).pipe(
      tap((sdps: ServiceDeliveryPoint[]) => {
        const convertedSdps = convertKeys.toCamel<ServiceDeliveryPoint[]>(sdps);

        this.sdpsCache.set(siteId, convertedSdps);

        isUpdatingList &&
          this.serviceDeliveryPoints$.next({
            sdps: convertedSdps,
            hasError: false,
            selectFirst: forceSelectFirst,
            selectSdpId: selectSdpId,
            isLoading: false,
          });

        if (selectSdpId) {
          this.selectSDP(selectSdpId);
        }
      }),
      catchError((err: unknown) => {
        const GLOBAL_GET_SDPS_ERROR = this.translateService.instant('utility.account.get.sdps.global-error');
        const LIST_GET_SDPS_ERROR = this.translateService.instant('utility.account.get.sdps.error');

        isUpdatingList &&
          this.serviceDeliveryPoints$.next({
            sdps: [],
            hasError: true,
            errorMessage: LIST_GET_SDPS_ERROR,
            isLoading: false,
          });

        this.messageService.setError(GLOBAL_GET_SDPS_ERROR);

        throw err;
      }),
    );
  }

  public async getSDPServiceAccounts(sdpId: string): Promise<void> {
    try {
      this.loadingServiceAccounts$.next(true);
      const serviceAccs = await this.apiService.get(`service-delivery-points/${sdpId}/service-accounts`);
      this.serviceAccounts$.next(convertKeys.toCamel(serviceAccs));
      this.loadingServiceAccounts$.next(false);
    } catch (err) {
      console.log(`Could not retrieve Service Accounts from SDP with id: ${sdpId}`);
      this.serviceAccounts$.next([]);
      this.loadingServiceAccounts$.next(false);
    }
  }

  public async createServiceDeliveryPoint(
    serviceDeliveryPoint: ServiceDeliveryPoint,
    siteId: string,
  ): Promise<ServiceDeliveryPoint> {
    try {
      const dto = convertKeys.toSnake<any>(serviceDeliveryPoint);
      dto.display_labels = serviceDeliveryPoint.displayLabels;
      dto.descriptions = serviceDeliveryPoint.descriptions;
      dto.address1 = serviceDeliveryPoint.address1;
      delete dto.address_1;
      if (dto.address_2) {
        dto.address2 = serviceDeliveryPoint.address2;
      }
      delete dto.address_2;
      const createdServiceDeliveryPoint = await this.apiService.post('service-delivery-points', dto);
      const relationships = {
        related_id: createdServiceDeliveryPoint.id,
        space_id: siteId,
      };
      await this.apiService.post('relationships', relationships);
      return convertKeys.toCamel<any>(createdServiceDeliveryPoint);
    } catch (err) {
      console.log(`Could not Create ServiceDeliveryPoint`, err);
      throw err;
    }
  }

  public async deleteServiceDeliveryPoint(serviceDeliveryPointId: string, siteId: string): Promise<any> {
    const deletedServiceDeliveryPoint = await this.apiService.delete(
      `sites/${siteId}/service-delivery-points/${serviceDeliveryPointId}`,
    );
    return convertKeys.toCamel<any>(deletedServiceDeliveryPoint);
  }

  public async updateServiceDeliveryPoint(
    serviceDeliveryPoint: ServiceDeliveryPoint,
    siteId: string,
    selectedSite: string,
  ): Promise<any> {
    try {
      const serviceDeliveryPointId = serviceDeliveryPoint.id;
      const dto = convertKeys.toSnake<any>(serviceDeliveryPoint);
      dto.descriptions = serviceDeliveryPoint.descriptions;
      dto.display_labels = serviceDeliveryPoint.displayLabels;
      const updatedServiceDeliveryPoint = await this.apiService.put(
        `service-delivery-points/${serviceDeliveryPointId}`,
        dto,
      );
      if (siteId !== selectedSite) {
        await this.apiService.delete(`relationships/${selectedSite}/${serviceDeliveryPointId}`);
        const relationships = {
          related_id: serviceDeliveryPointId,
          space_id: siteId,
        };
        await this.apiService.post('relationships', relationships);
      }

      return convertKeys.toCamel<any>(updatedServiceDeliveryPoint);
    } catch (err) {
      console.log(`Could not Update ServiceDeliveryPoint`, err);
      throw err;
    }
  }

  public getCachedSDPs(siteId: string): ServiceDeliveryPoint[] | null {
    return this.sdpsCache.get(siteId) || null;
  }

  public hasCachedSDPs(siteId: string): boolean {
    return this.isCacheValid(siteId);
  }

  public clearCache(siteId?: string): void {
    if (siteId) {
      this.sdpsCache.delete(siteId);
    } else {
      this.sdpsCache.clear();
    }
  }

  public refreshSDPs(
    siteId: string,
    isUpdatingList: boolean = true,
    forceSelectFirst: boolean = true,
    selectSdpId?: string,
  ): Observable<ServiceDeliveryPoint[]> {
    return this.getServiceDeliveryPoints(siteId, isUpdatingList, forceSelectFirst, true, selectSdpId);
  }

  public selectSDP(sdpId: string): void {
    this.selectedServiceDeliveryPointId$.next(sdpId);

    if (this.sdpParentId && this.sdpsCache.has(this.sdpParentId)) {
      const currentData = this.serviceDeliveryPoints$.getValue();

      if (currentData && !currentData.hasError) {
        this.serviceDeliveryPoints$.next({
          ...currentData,
          selectSdpId: sdpId,
          selectFirst: false,
        });
      }
    }
  }

  private isCacheValid(siteId: string): boolean {
    return this.sdpsCache.has(siteId);
  }

  public async getParentSiteId(orgId: string, sdpId: string): Promise<string> {
    try {
      const response = await this.pagesLoaderService.getAllPagesForType('servicedeliverypoint', {
        id: orgId,
        dr_type: 'organization',
      });
      const sdps = convertKeys.toCamel<ServiceDeliveryPoint[]>(response.types);
      const sdp = sdps.find((sdp) => sdp.id === sdpId);
      return sdp ? sdp.parentId : null;
    } catch (err) {
      console.log('Could not retrieve parent site id for SDP.', err);
      return null;
    }
  }
}
